<template>
    <div
        class="picker-row skin-pokemon-card"
        :class="{'picker-row--disabled': pokemon.hp < 1 && !allowFainted, 'picker-row--donate': pokemon.hp < 1 && allowFainted}"
        @click="!disabled && $emit('click')"
    >
        <div v-if="pokemon.hp < 1 && allowFainted" class="picker-row-donate-label">{{ $t('journey.pokemonPickerCard.giveUpLabel') }}</div>
        <div class="picker-row-img-wrap">
            <v-img :src="icon" contain class="picker-row-img"/>
        </div>
        <div class="picker-row-body">
            <div class="picker-row-header">
                <!-- Zadani uzivatele 2026-08-27 - level vedle jmena, ne v rohu obrazku.
                     Jeden SVG element (zadani uzivatele 2026-08-28) - kruh/
                     hvezda/cislo ve stejne souradnicove soustave (viewBox),
                     skaluje se jako jeden celek. Puvodni varianta (kruh jako
                     CSS pozadi + samostatny <v-icon> hvezda + <span> cislo,
                     obojí nezavisle centrovane pres position:absolute) se na
                     malem 17px kolecku pri mensich rozlisenich vizualne
                     rozjizdela (subpixelove zaokrouhlovani dvou ruznych HTML
                     elementu). SVG nema tenhle problem - vsechny 3 vrstvy
                     jsou definovane ve stejnych souradnicich, prohlizec je
                     skaluje spolu, ne kazdou zvlast. -->
                <svg class="picker-row-level" viewBox="0 0 24 24">
                    <defs>
                        <radialGradient :id="levelGradientId" cx="30%" cy="30%" r="75%">
                            <stop offset="0%" stop-color="#ffffff" />
                            <stop offset="100%" stop-color="#494949" />
                        </radialGradient>
                    </defs>
                    <circle cx="12" cy="12" r="12" :fill="`url(#${levelGradientId})`" />
                    <!-- 5cipa hvezda, vnejsi polomer 11 (kruh ma r=12) - cipy
                     tesne u okraje kolecka, zadani uzivatele 2026-08-28
                     ("hvezdicka neni videt, musi mit cipy az k okraji"). -->
                    <path
                        d="M12,1L14.53,8.52L22.46,8.6L16.09,13.33L18.47,20.9L12,16.3L5.53,20.9L7.91,13.33L1.54,8.6L9.47,8.52Z"
                        fill="#ffebbc"
                    />
                    <text x="12" y="16.3" text-anchor="middle" :font-size="levelFontSize" class="picker-row-level-text">{{ pokemon.level }}</text>
                </svg>
                <span class="picker-row-name">{{ pokemon.name || pokemon.pokemonType.name }}</span>
                <TypePill :type="pokemon.pokemonType.type" size="small" class="picker-row-type">{{ $t('common.typeNames.' + pokemon.pokemonType.type.name) }}</TypePill>
            </div>
            <div class="picker-row-bars">
                <PokemonProgressBar
                    v-if="pokemon.evolveProgress !== undefined && pokemon.evolveProgress !== null"
                    :percent="pokemon.evolveProgress"
                    variant="evolve"
                    :height="3"
                />
                <v-progress-linear
                    class="picker-row-hp caption pa-0 ma-0"
                    background-color="bar-hp-red"
                    color="bar-hp-green"
                    height="16"
                    rounded
                    :value="hpPercent"
                >
                    <strong class="picker-row-hp-text">{{ pokemon.hp }}/{{ pokemon.maxHp }}</strong>
                </v-progress-linear>
                <PokemonProgressBar
                    v-if="pokemon.levelProgress !== undefined && pokemon.levelProgress !== null"
                    :percent="pokemon.levelProgress"
                    variant="level"
                    :height="3"
                />
            </div>
            <!-- Zadani uzivatele 2026-08-27 - lebka AZ ZA bary, ne u jmena. -->
            <div v-if="pokemon.endangered" class="picker-row-endangered-row">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon size="15" color="red" v-bind="attrs" v-on="on">mdi-skull</v-icon>
                    </template>
                    <p style="width: 150px; margin: 0;">{{ $t('pokemon.common.endangeredTooltip') }}</p>
                </v-tooltip>
            </div>
            <div class="picker-row-footer">
                <div class="picker-row-exp" v-if="pokemon.pokemonExperience">
                    <v-icon size="13" class="picker-row-exp-icon">mdi-chart-line</v-icon>{{ pokemon.pokemonExperience.exp }}
                </div>
                <div class="picker-row-free-exp" v-if="pokemon.pokemonExperience">
                    <v-icon size="13" color="#fff" class="picker-row-exp-icon">mdi-star-box</v-icon>{{ pokemon.pokemonExperience.freeExp }}
                </div>
            </div>
        </div>

        <!-- Zadani uzivatele 2026-08-27 - presunuto mimo picker-row-footer, at
             stoji jako samostatny sloupec uplne vpravo celeho radku, 2x vyssi
             (viz .picker-row-skills-btn) misto puvodniho maleho tlacitka
             vedle exp ikon. Sdilena trida "skin-btn-skills" (stejna jako
             skills tlacitko na PokemonDetail.vue/PokemonManager.vue/
             OakTutorialNext.vue/TrainerBag.vue, definovana v
             skins/normal.css a skins/hitech.css) misto vlastni Vuetify
             barvy - stejna seda i text napric obema skiny/tematy. -->
        <v-btn x-small class="skin-btn-skills picker-row-skills-btn" :loading="skillsLoading" @click.stop="toggleSkills">
            {{ $t('pokemon.common.skillsButton') }}
        </v-btn>

        <!-- Skill dialog - stejny princip jako PokemonDetail.vue (allowBuySkills=false):
             jen skilly, ktere pokemon UMI, zadny nakup. Lazy fetch az na klik. -->
        <ResponsiveDialog
            v-model="skillsDialogOpen"
            :max-width="820"
            :full-height="false"
            :header-title="$t('pokemon.pokemonDetail.skillsDialogTitle', {name: pokemon.pokemonType.name})"
            header-class="skin-window-header--skillpopup"
            card-class="skin-card"
        >
            <div class="skin-window--skillpopup pokemon-detail-skills-bg">
                <v-card-text class="transparent03 justify-center pokemon-detail-skills-content">
                    <SkillListTable
                        v-if="skills && skills.length > 0"
                        :items="skills"
                        mode="view"
                        :disabled-skill-ids="dungeonUsedSkillIds"
                        @open-detail="openSkillDetail"
                    />
                    <p v-else style="text-align:center;">{{ $t('pokemon.pokemonDetail.noSkillsMessage', {name: pokemon.name}) }}</p>
                </v-card-text>
            </div>
        </ResponsiveDialog>
        <SkillDetailPopup v-model="skillDetailOpen" :skill="selectedSkill" />
    </div>
</template>

<script>
    // Kompaktni radek pro PokemonPickerDialog.vue - zadani uzivatele
    // 2026-08-27 "picker jako u Oaka": stejna data jako plny detail
    // (PokemonDetail.vue) - endangered/hp/level/evolve+level bar/exp/skilly -
    // jen v uzsim radkovem layoutu (misto velke karty), at se jich 6 vejde na
    // jednu obrazovku (viz PokemonPickerDialog.vue).
    import {mapActions} from 'vuex';
    import TypePill from '@/components/utils/TypePill';
    import PokemonProgressBar from '@/components/pokemon/PokemonProgressBar';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import SkillListTable from '@/components/pokedex/SkillListTable';
    import SkillDetailPopup from '@/components/pokedex/SkillDetailPopup';
    import {pokemonImageSrc} from '@/utils/pokemonImage';
    import {API_ENTRYPOINT} from '@/config/entrypoint';

    export default {
        name: 'PokemonPickerCard',
        components: {TypePill, PokemonProgressBar, ResponsiveDialog, SkillListTable, SkillDetailPopup},
        props: {
            pokemon: {type: Object, required: true},
            disabled: {type: Boolean, default: false},
            // Normalne jsou 0 HP karty pointer-events:none (nejde vybrat
            // mrtveho pokemona do souboje). Tenhle prop je pro 0 HP odblokuje
            // a prebarvi na "Give up" variantu - klik v BattleScreen.vue jde
            // na donate-pokemon-team-rocket misto add-to-fight.
            allowFainted: {type: Boolean, default: false},
            // Dungeon "dnes jeste dostupne" pool (Position::trainerPokemonSkills,
            // zadani uzivatele 2026-08-27) - null mimo dungeon (zadne sedivěni),
            // pole ID skillu v dungeonu. Viz dungeonUsedSkillIds nize.
            dungeonAvailableSkillIds: {type: Array, default: null},
        },
        data() {
            return {
                skillsDialogOpen: false,
                skillsLoading: false,
                // null = jeste nestazeno (stejny vzor jako PokemonDetail.vue) -
                // pokemon.skills (pokud uz na objektu je z jine serializacni
                // skupiny) se schvalne nepredpoklada, vzdy dotahne cerstve.
                skills: null,
                selectedSkill: null,
                skillDetailOpen: false,
            }
        },
        computed: {
            icon() {
                return pokemonImageSrc(this.pokemon.pokemonType.img);
            },
            hpPercent() {
                return parseInt(this.pokemon.hp) / parseInt(this.pokemon.maxHp) * 100;
            },
            // Skilly, ktere pokemon UMI, ale dungeon pool je uz dnes vycerpal
            // (mimo dungeon dungeonAvailableSkillIds === null => vzdy []).
            // String() kvuli simple_array na BE (pole stringu), skill.id je cislo.
            dungeonUsedSkillIds() {
                if (this.dungeonAvailableSkillIds === null || !this.skills) {
                    return [];
                }
                const availableIds = this.dungeonAvailableSkillIds.map(String);
                return this.skills
                    .filter((s) => !availableIds.includes(String(s.id)))
                    .map((s) => s.id);
            },
            // Vuetify komponenta muze mit v pickeru vic instanci najednou
            // (jeden radek = jeden pokemon) - gradient v SVG <defs> potrebuje
            // unikatni id, jinak by vsechny <circle fill="url(#...)"> na
            // strance odkazovaly na tu UPLNE PRVNI <radialGradient> v DOM.
            levelGradientId() {
                return 'picker-level-grad-' + this._uid;
            },
            // Font-size cisla levelu (SVG viewBox 0-24 jednotky, ne px) podle
            // poctu cislic - stejny pomer jako TeamBottomSheet.vue::levelFontSize,
            // at se vejde do kruhu.
            levelFontSize() {
                const len = String(this.pokemon.level).length;
                if (len <= 1) return 13;
                if (len === 2) return 10;
                return 8;
            },
        },
        methods: {
            ...mapActions({
                fetchPokemon: 'pokemon/fetchObject',
            }),
            toggleSkills() {
                if ((this.skills === undefined || this.skills === null) && !this.skillsDialogOpen) {
                    this.downloadSkills();
                } else {
                    this.skillsDialogOpen = !this.skillsDialogOpen;
                }
            },
            downloadSkills() {
                this.skillsLoading = true;
                const url = new URL('api/pokemon-skills/{id}'.replace('{id}', this.pokemon.id), API_ENTRYPOINT);
                this.fetchPokemon({url, method: 'GET'}).then((data) => {
                    this.skills = data.skills;
                    this.skillsLoading = false;
                    this.skillsDialogOpen = true;
                });
            },
            openSkillDetail(skill) {
                this.selectedSkill = skill;
                this.skillDetailOpen = true;
            },
        },
    }
</script>

<style scoped>
.picker-row {
    width: 100%;
    height: 100%;
    padding: 2px 8px;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    box-sizing: border-box;
    position: relative;
}
.picker-row--disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
}
.picker-row--donate {
    opacity: 0.75;
    outline: 2px solid var(--v-error-base, #EF5167);
    outline-offset: -2px;
}
.picker-row-donate-label {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    background: var(--v-error-base, #EF5167);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 999px;
}
.picker-row-img-wrap {
    flex: none;
    width: 64px;
    position: relative;
    display: flex;
    align-items: center;
}
.picker-row-img {
    width: 100%;
    height: 100%;
}
.picker-row-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
}
.picker-row-header {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}
.picker-row-name {
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.picker-row-type {
    flex: none;
    font-size: 11px;
    zoom: 0.75;
}
.picker-row-bars {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.picker-row-hp-text {
    font-size: 11px;
}
.picker-row-endangered-row {
    display: flex;
    justify-content: flex-end;
}
.picker-row-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
}
.picker-row-exp,
.picker-row-free-exp {
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    white-space: nowrap;
}
.picker-row-exp-icon {
    margin-right: 2px;
}
/* Uplne vpravo celeho radku, mimo textovy sloupec - viz komentar u sablony.
   Vyska 30px = 40px (2x puvodni x-small 20px) - 25 % dle zadani uzivatele 2026-08-28. */
.picker-row-skills-btn {
    flex: none;
    align-self: center;
    height: 30px !important;
}

/* Stejny vizual jako .team-sheet-level-badge v TeamBottomSheet.vue - staticky
   badge vedle jmena v hlavicce, ne absolutne v rohu obrazku (zadani
   uzivatele 2026-08-27). Jeden SVG (kruh/hvezda/cislo ve stejnem viewBoxu,
   zadani uzivatele 2026-08-28) misto CSS pozadi + samostatnych HTML vrstev -
   viz komentar u sablony, proc puvodni varianta na malych rozlisenich
   vizualne rozjizdela hvezdicku od cisla. */
.picker-row-level {
    flex: none;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    box-shadow: 1px 2.5px 6px rgba(243,224,184,0.6);
}
.picker-row-level-text {
    fill: #000;
    font-weight: 900;
}

.theme--light .picker-row-name,
.theme--light .picker-row-hp-text,
.theme--light .picker-row-exp {
    color: #000;
}
.theme--dark .picker-row-name,
.theme--dark .picker-row-hp-text,
.theme--dark .picker-row-exp {
    color: #fff;
}
</style>
